import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { adminUsersRepositoryToken } from './repository';
import type { PermissionDefinition, PermissionProfile } from './types';

interface Values {
  readonly partnerId: string;
  readonly agentId: string;
  readonly name: string;
  readonly permissions: string[];
  readonly reason: string;
}

export function PermissionProfileDialog({
  onClose,
  onSaved,
  open,
  permissions,
  profile,
}: {
  readonly onClose: () => void;
  readonly onSaved: (profile: PermissionProfile) => void;
  readonly open: boolean;
  readonly permissions: readonly PermissionDefinition[];
  readonly profile?: PermissionProfile;
}) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<Values>({ defaultValues: defaults(profile) });
  useEffect(() => {
    if (open) {
      reset(defaults(profile));
      setError(null);
      guard.current = false;
      setBusy(false);
    }
  }, [open, profile, reset]);
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async (value) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const input = {
        name: value.name.trim(),
        permissions: value.permissions,
        reason: value.reason.trim(),
      };
      const result =
        profile === undefined
          ? await repository.createProfile({
              partnerId: value.partnerId.trim(),
              agentId: value.agentId.trim(),
              ...input,
            })
          : await repository.updateProfile(profile.id, input);
      onSaved(result.data);
      onClose();
    } catch (cause) {
      setError(toDialogError(cause, t('adminUsers.errors.profileMutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  const required = {
    required: t('adminUsers.validation.required'),
    validate: (value: string) => value.trim().length > 0 || t('adminUsers.validation.required'),
  };
  return (
    <Dialog fullWidth maxWidth="md" onClose={close} open={open}>
      <DialogTitle>
        {t(profile === undefined ? 'adminUsers.profiles.create' : 'adminUsers.profiles.edit')}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="permission-profile-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="info">{t('adminUsers.profiles.delegableOnly')}</Alert>
          {profile === undefined ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                error={!!errors.partnerId}
                helperText={errors.partnerId?.message}
                label={t('adminUsers.fields.partnerId')}
                required
                {...register('partnerId', required)}
              />
              <TextField
                error={!!errors.agentId}
                helperText={errors.agentId?.message}
                label={t('adminUsers.fields.agentId')}
                required
                {...register('agentId', required)}
              />
            </Stack>
          ) : null}
          <TextField
            error={!!errors.name}
            helperText={errors.name?.message}
            label={t('adminUsers.fields.profile')}
            required
            {...register('name', required)}
          />
          <PermissionGroups
            permissions={permissions.filter((permission) => permission.delegable)}
            register={register}
          />
          <TextField
            error={!!errors.reason}
            helperText={errors.reason?.message}
            label={t('adminUsers.fields.reason')}
            multiline
            required
            {...register('reason', required)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="permission-profile-form" loading={busy} type="submit" variant="contained">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PermissionGroups({
  permissions,
  register,
}: {
  readonly permissions: readonly PermissionDefinition[];
  readonly register: ReturnType<typeof useForm<Values>>['register'];
}) {
  const { t } = useTranslation();
  const domainLabels: Readonly<Record<string, string>> = {
    AUDIT: t('adminUsers.profiles.domains.audit'),
    MEMBER: t('adminUsers.profiles.domains.member'),
    TRANSACTION: t('adminUsers.profiles.domains.transaction'),
    WALLET: t('adminUsers.profiles.domains.wallet'),
  };
  const groups = permissions.reduce<Record<string, PermissionDefinition[]>>(
    (result, permission) => {
      const domain = permissionDomain(permission.code);
      result[domain] = [...(result[domain] ?? []), permission];
      return result;
    },
    {},
  );
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ alignItems: 'stretch', flexWrap: 'wrap' }}
    >
      {Object.entries(groups).map(([domain, entries]) => (
        <Paper
          aria-label={domainLabel(domain, domainLabels)}
          component="fieldset"
          key={domain}
          sx={{ flex: '1 1 220px', m: 0, p: 2 }}
          variant="outlined"
        >
          <Typography component="legend" variant="subtitle2">
            {domainLabel(domain, domainLabels)}
          </Typography>
          <FormGroup>
            {entries?.map((permission) => (
              <FormControlLabel
                control={<Checkbox value={permission.code} {...register('permissions')} />}
                key={permission.code}
                label={`${permission.code} — ${permission.description}${permission.sensitive ? ` · ${t('adminUsers.profiles.sensitive')}` : ''}`}
              />
            ))}
          </FormGroup>
        </Paper>
      ))}
    </Stack>
  );
}

function defaults(profile?: PermissionProfile): Values {
  return {
    partnerId: profile?.partnerId ?? '',
    agentId: profile?.agentId ?? '',
    name: profile?.name ?? '',
    permissions: profile === undefined ? [] : [...profile.permissions],
    reason: '',
  };
}
function permissionDomain(code: string): string {
  return code.split('_', 1)[0] ?? 'OTHER';
}
function domainLabel(domain: string, labels: Readonly<Record<string, string>>): string {
  return labels[domain] ?? domain.charAt(0) + domain.slice(1).toLocaleLowerCase();
}
